import React, { useCallback, useMemo } from "react";
import { Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { BRAND_HERO_BLUE, BRAND_MEDIUM_BLUE, CHARGEBLUE_80, WHITE } from "../../design-tokens";
import useStyles from "../../hooks/useStyles";
import { Typography } from "../Typography";
const BRAND_CHARGE_BLUE_80 = CHARGEBLUE_80;
const createStyles = _ref => {
  let {
    stylesProps
  } = _ref;
  const {
    type,
    size,
    isDisabled,
    iconLeft,
    iconMiddle,
    iconRight
  } = stylesProps;
  const opacity = isDisabled ? 0.3 : undefined;
  const position = (iconLeft || iconRight) && "side" || iconMiddle && "middle" || !iconLeft && !iconMiddle && !iconRight && "text";
  const width = position !== "middle" ? "100%" : "auto";
  const justifyContent = position === "side" ? "space-between" : "space-evenly";
  const paddingStyle = {
    side: {
      xs: {
        paddingTop: 3,
        paddingBottom: 3,
        paddingLeft: 23,
        paddingRight: 23
      },
      sm: {
        paddingTop: 8,
        paddingBottom: 6,
        paddingLeft: 23,
        paddingRight: 23
      },
      md: {
        paddingTop: 18,
        paddingBottom: 16,
        paddingLeft: 23,
        paddingRight: 23
      },
      lg: {
        paddingTop: 24,
        paddingBottom: 22,
        paddingLeft: 23,
        paddingRight: 23
      }
    },
    middle: {
      xs: {
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 5,
        paddingRight: 5
      },
      sm: {
        paddingTop: 9,
        paddingBottom: 9,
        paddingLeft: 9,
        paddingRight: 9
      },
      md: {
        paddingTop: 19,
        paddingBottom: 19,
        paddingLeft: 19,
        paddingRight: 19
      },
      lg: {
        paddingTop: 25,
        paddingBottom: 25,
        paddingLeft: 25,
        paddingRight: 25
      }
    },
    text: {
      xs: {
        paddingTop: 3,
        paddingBottom: 3,
        paddingLeft: 19,
        paddingRight: 19
      },
      sm: {
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 19,
        paddingRight: 19
      },
      md: {
        paddingTop: 17,
        paddingBottom: 17,
        paddingLeft: 19,
        paddingRight: 19
      },
      lg: {
        paddingTop: 23,
        paddingBottom: 23,
        paddingLeft: 19,
        paddingRight: 19
      }
    }
  };
  const colorStyles = {
    primary: {
      hover: {
        backgroundColor: BRAND_MEDIUM_BLUE
      },
      focus: {
        borderColor: BRAND_HERO_BLUE
      }
    },
    secondary: {
      hover: {
        color: BRAND_MEDIUM_BLUE,
        borderColor: BRAND_MEDIUM_BLUE
      },
      focus: {
        color: BRAND_MEDIUM_BLUE,
        borderColor: BRAND_HERO_BLUE
      }
    },
    tertiary: {
      hover: {
        backgroundColor: BRAND_CHARGE_BLUE_80,
        color: BRAND_MEDIUM_BLUE
      },
      focus: {
        backgroundColor: BRAND_CHARGE_BLUE_80,
        color: BRAND_MEDIUM_BLUE,
        borderColor: BRAND_MEDIUM_BLUE
      }
    }
  };
  let backgroundColor;
  let borderColor;
  let color;
  switch (type) {
    case "primary":
      backgroundColor = BRAND_HERO_BLUE;
      color = WHITE;
      borderColor = "transparent";
      break;
    case "secondary":
      backgroundColor = WHITE;
      color = BRAND_HERO_BLUE;
      borderColor = BRAND_HERO_BLUE;
      break;
    case "tertiary":
      backgroundColor = WHITE;
      color = BRAND_HERO_BLUE;
      borderColor = "transparent";
      break;
    default:
      break;
  }
  return StyleSheet.create({
    wrapper: {
      display: "flex",
      flexDirection: "row",
      width: stylesProps.width
    },
    eventWrapper: {
      width
    },
    button: {
      display: "flex",
      flexDirection: "row",
      justifyContent,
      alignItems: "center",
      ...paddingStyle[position][size],
      backgroundColor,
      borderRadius: 800,
      borderWidth: 1,
      borderColor,
      color,
      opacity
    },
    text: {
      color
    },
    iconLeft: {
      paddingRight: 20
    },
    iconRight: {
      paddingLeft: 20
    },
    ...colorStyles[type]
  });
};
const Button = _ref2 => {
  let {
    children,
    type = "primary",
    size = "lg",
    width,
    isDisabled = false,
    iconLeft,
    iconMiddle,
    iconRight,
    onPress,
    onLongPress,
    onHoverIn,
    onHoverOut,
    onFocus,
    onBlur
  } = _ref2;
  const [isHover, setIsHover] = React.useState(false);
  const [isFocus, setIsFocus] = React.useState(false);

  // Styles
  const stylesProps = useMemo(() => {
    return {
      type,
      size,
      width,
      isDisabled,
      iconLeft,
      iconMiddle,
      iconRight
    };
  }, [type, size, width, isDisabled, iconLeft, iconMiddle, iconRight]);
  const styles = useStyles(createStyles, stylesProps);
  const buttonStyles = useMemo(() => {
    return styles.button;
  }, [styles.button]);
  const hoverStyles = useMemo(() => {
    return isHover && styles.hover;
  }, [isHover, styles.hover]);
  const focusStyles = useMemo(() => {
    return isFocus && styles.focus;
  }, [isFocus, styles.focus]);
  const textStyles = useMemo(() => {
    return styles.text;
  }, [styles.text]);
  const iconLeftStyles = useMemo(() => [styles.iconLeft], [styles.iconLeft]);
  const iconRightStyles = useMemo(() => [styles.iconRight], [styles.iconRight]);

  // Events
  const onPressIntent = useCallback(() => {
    if (onPress) {
      onPress();
    }
  }, [onPress]);
  const onLongPressIntent = useCallback(() => {
    if (onLongPress) {
      onLongPress();
    }
  }, [onLongPress]);
  const onHoverInIntent = useCallback(() => {
    setIsHover(true);
    if (onHoverIn) {
      onHoverIn();
    }
  }, [setIsHover]);
  const onHoverOutIntent = useCallback(() => {
    setIsHover(false);
    if (onHoverOut) {
      onHoverOut();
    }
  }, [setIsHover]);
  const onFocusIntent = useCallback(() => {
    setIsFocus(true);
    if (onFocus) {
      onFocus();
    }
  }, [setIsFocus]);
  const onBlurIntent = useCallback(() => {
    setIsFocus(false);
    if (onBlur) {
      onBlur();
    }
  }, [setIsFocus]);
  return /*#__PURE__*/React.createElement(View, {
    style: styles.wrapper
  }, /*#__PURE__*/React.createElement(Pressable, {
    style: styles.eventWrapper,
    onHoverIn: onHoverInIntent,
    onHoverOut: onHoverOutIntent,
    onFocus: onFocusIntent,
    onBlur: onBlurIntent
  }, /*#__PURE__*/React.createElement(TouchableOpacity, {
    style: [buttonStyles, hoverStyles, focusStyles],
    disabled: isDisabled,
    onPress: onPressIntent,
    onLongPress: onLongPressIntent
  }, iconLeft && /*#__PURE__*/React.createElement(View, {
    style: iconLeftStyles
  }, iconLeft), iconMiddle && /*#__PURE__*/React.createElement(View, null, iconMiddle), !iconMiddle && /*#__PURE__*/React.createElement(Typography, {
    variant: "span",
    style: [textStyles, hoverStyles]
  }, children), iconRight && /*#__PURE__*/React.createElement(View, {
    style: iconRightStyles
  }, iconRight))));
};
export { Button };